import { useApiQuery } from "@gladis/api-client";
import {
  ExpenseForm,
  ExpenseTable,
  useExpensesSchedule,
  type ExpenseRowDto,
} from "@gladis/domain-expenses/ui";
import {
  DisposalDialog,
  ValuationForm,
  ValuationHistory,
  useUnlinkCost,
  useVehicleDetail,
} from "@gladis/domain-vehicles/ui";
import type { CurrentUserDto, ExpensesPageDto, MemberDto } from "@gladis/shared";
import { formatEur } from "@gladis/shared";
import { ActionMenu, Button, EmptyState, ErrorState, LoadingState, Panel } from "@gladis/ui";
import { formatDate, useT } from "@gladis/ui/i18n";
import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { Page } from "../../../components/page.js";

export const Route = createFileRoute("/_authenticated/_shell/vehicles_/$vehicleId")({
  component: VehicleDetail,
});

/**
 * One vehicle: what it is, what it is worth, and what it costs.
 *
 * THE `_` IN THE FILENAME OPTS OUT OF NESTING. Without it TanStack makes this a child of
 * `/vehicles`, which would need an `<Outlet />` there and render the list above the detail. The same
 * note is on the savings, properties and investments detail routes; it is the kind of thing that
 * costs an hour the first time and no time ever again once written down.
 *
 * ==========================================================================================
 * THIS IS THE PAGE WHERE A VEHICLE'S COSTS ARE ENTERED, USING THE EXPENSES MODULE'S OWN FORM
 * ==========================================================================================
 *
 * That was the point of building it. The list page previously offered a dialog per row listing every
 * recurring expense in the household with an Attach button beside each — the wrong question, asked
 * in a control that grew with the household's whole expense list. Here a member adds "insurance"
 * underneath the car, and what is written is an ordinary recurring expense plus a link.
 *
 * **It is `ExpenseForm`, not a copy of it**, pointed at `/vehicles/:id/costs` through its
 * `destination` prop. A vehicle's cost IS a recurring expense, so the form for entering one is the
 * form for entering one: the same card picker, the same split editor, the same category catalogue,
 * the same seventeen refusals.
 *
 * A second form lived in the vehicles package for a few hours and had already drifted — no card
 * picker, no splits, and a monthly preview an architecture test refused. Each of those would have
 * had to be re-added and re-argued, and the card was reported missing before the day was out.
 *
 * THIS ROUTE IS WHERE THE TWO CONTEXTS MEET, and it is the only place they may. Neither package
 * imports the other: `ExpenseForm` is handed a function and never learns what a vehicle is, and
 * `@gladis/domain-vehicles` still has no idea what a recurrence interval is.
 *
 * **The rows are not private to this page.** They appear on the Expenses page inside this vehicle's
 * foldable group, count once in the household total, and are edited and archived there like any
 * other. `vehicles-hold-no-costs.test.ts` is what keeps that true.
 *
 * ==========================================================================================
 * ONE PRIMARY BUTTON AND A MENU, WHICH IS THE PROPERTY PAGE'S ARRANGEMENT
 * ==========================================================================================
 *
 * ADD A COST is primary because it is the act this page exists for. Recording a value and recording
 * a sale sit behind `ActionMenu` — where the labels can stay long enough to mean something, which
 * matters most for "Record what it sold for", an irreversible act that must not abbreviate to
 * something a member could mistake for the reversible one beside it.
 *
 * DIALOGS ARE MOUNTED ONLY WHILE OPEN. 016's E2E found the bug this rule exists for: a dialog left
 * mounted keeps the `useState` from the first time it was opened, so a second entry starts with the
 * first one's values still in the fields.
 */
function VehicleDetail(): ReactNode {
  const t = useT();
  const { vehicleId } = Route.useParams();
  const detail = useVehicleDetail(vehicleId);

  /*
   * THE MEMBERS AND THE CARDS THE COST FORM NEEDS, from the reads that already own them.
   *
   * The cards come from the Expenses page's payload rather than a read of this module's own: cards
   * belong to expenses, and a copy on the vehicles response would be a second answer to "which cards
   * exist". TanStack dedupes it against that page's query, so a member who has been there pays
   * nothing, and the vehicles detail read keeps its ten-query budget exactly.
   */
  const me = useApiQuery<CurrentUserDto>(["me"], "/me");
  const householdId = me.data?.activeHousehold?.id;
  const memberList = useApiQuery<{ members: MemberDto[] }>(
    ["members", householdId ?? "none"],
    `/households/${householdId}/members`,
    { enabled: householdId !== undefined },
  );
  const expenses = useApiQuery<ExpensesPageDto>(["expenses", {}], "/expenses");
  /*
   * THE COSTS' DUE DATES (feature 024), from the expenses schedule — the same read the Expenses page opens
   * on, household-wide. A cost IS a recurring expense, so its "Next due" and the date its edit form starts
   * from are the expenses side's, keyed by the expense id; without `currentAnchor` the form would open a
   * dated cost with an empty field and refuse to let its interval change.
   */
  const schedule = useExpensesSchedule("month");

  const [dialog, setDialog] = useState<"cost" | "valuation" | "sell" | null>(null);
  /*
   * THE COST BEING EDITED, and editing one opens the SAME form that adds one — with no destination,
   * because an edit is `PATCH /api/expenses/:id` wherever it was opened from. The link is not being
   * touched, so there is nothing vehicle-shaped about the request.
   */
  const [editingCost, setEditingCost] = useState<ExpenseRowDto | null>(null);
  const close = () => {
    setDialog(null);
    setEditingCost(null);
  };

  /** Removes the STATEMENT that a cost belongs to this vehicle. The expense itself is untouched. */
  const unlink = useUnlinkCost();

  const vehicle = detail.data?.vehicle;
  const sold = vehicle?.disposal !== null && vehicle !== undefined;

  return (
    <Page>
      <Page.Title
        actions={
          vehicle === undefined ? null : (
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                disabled={sold}
                onClick={() => {
                  setDialog("cost");
                }}
              >
                {t("veh.cost.add")}
              </Button>
              <ActionMenu
                label={t("veh.list.actionsFor", { name: vehicle.name })}
                items={[
                  {
                    label: t("veh.list.recordValue"),
                    disabled: sold,
                    onSelect: () => {
                      setDialog("valuation");
                    },
                  },
                  {
                    label: t("veh.list.sell"),
                    disabled: sold,
                    onSelect: () => {
                      setDialog("sell");
                    },
                  },
                ]}
              />
            </div>
          )
        }
      >
        {vehicle?.name ?? t("veh.page.title")}
      </Page.Title>

      <Page.Body>
        <div className="col-span-full flex flex-col gap-6">
          {detail.isPending ? <LoadingState>{t("identity.loading")}</LoadingState> : null}
          {detail.error ? <ErrorState>{detail.error.message}</ErrorState> : null}

          {vehicle && detail.data ? (
            <>
              {/*
                THE THREE FIGURES ARE NEVER SUMMED, and the two cost figures are two columns for the
                reason `VehicleList` argues at length: cash per month is money leaving the household,
                lost value per month is a balance changing, and adding them would put a fact about a
                balance into a figure that is a flow — the one thing 019 rebuilt the dashboard to
                stop doing.
              */}
              {/*
                FOUR FIGURES, AND NO TWO OF THEM ARE THE SAME KIND OF THING.
                  · Paid — a fact about a transaction, settled forever.
                  · Worth now — the newest observation, carried forward.
                  · Change — the two above subtracted. NOT depreciation.
                  · Cash per month — money leaving the household.

                The third and the module's `depreciation` figure are the pair most easily confused,
                and `gain.ts` argues the distinction at length: a dealer's margin is a real loss the
                moment you drive away and is not a measurement of how fast the car is falling. So
                "lost value per month" lives with the observations it is computed from, in the
                valuation history's own panel, and does not sit in this row pretending to be a
                fifth peer.
              */}
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Figure
                  label={t("veh.detail.paid")}
                  value={
                    vehicle.purchasePriceInEuros === null
                      ? t("veh.detail.paidUnknown")
                      : formatEur(vehicle.purchasePriceInEuros)
                  }
                  hint={t("veh.detail.paidHint")}
                />
                <Figure
                  label={t("veh.detail.worthNow")}
                  value={
                    vehicle.currentValueInEuros === null
                      ? t("veh.list.notValued")
                      : formatEur(vehicle.currentValueInEuros)
                  }
                  hint={
                    vehicle.valuedOn === null
                      ? undefined
                      : t("veh.detail.asAt", { date: formatDate(vehicle.valuedOn) })
                  }
                />
                <Figure
                  label={t("veh.detail.change")}
                  value={
                    vehicle.gain === null
                      ? t("veh.detail.changeUnknown")
                      : signedEur(vehicle.gain.amountInEuros)
                  }
                  hint={
                    /*
                     * The percentage is absent for a vehicle that was FREE, and the euro figure is
                     * still shown beside it — a gift now worth €300 has gained €300 and no
                     * percentage of anything. Rendering 0% or Infinity% there would be a lie on a
                     * page a household reads.
                     */
                    vehicle.gain === null || vehicle.gain.percent === null
                      ? t("veh.detail.changeHint")
                      : t("veh.detail.changePercent", {
                          percent: signedPercent(vehicle.gain.percent),
                        })
                  }
                />
                <Figure
                  label={t("veh.list.colCash")}
                  value={formatEur(vehicle.monthlyCostInEuros)}
                  hint={t("veh.detail.cashHint")}
                />
              </dl>

              <Panel title={t("veh.detail.facts")}>
                <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                  <Fact label={t("veh.form.type")} value={t(`veh.type.${vehicle.type}`)} />
                  <Fact
                    label={t("veh.form.firstRegistered")}
                    value={formatDate(vehicle.firstRegisteredOn)}
                  />
                  <Fact
                    label={t("veh.form.owner")}
                    value={vehicle.owner.displayName ?? t("veh.list.joint")}
                  />
                  {vehicle.disposal === null ? null : (
                    <Fact
                      label={t("veh.detail.sold")}
                      value={t("veh.detail.soldFor", {
                        amount: formatEur(vehicle.disposal.priceInEuros),
                        date: formatDate(vehicle.disposal.on),
                      })}
                    />
                  )}
                </dl>
              </Panel>

              <Panel title={t("veh.cost.heading2")} description={t("veh.cost.hint")}>
                {/*
                  THE EXPENSES TABLE, not a table of its own.
                  
                  Same six columns in the same order, the same category under the name, the same card
                  and "whose" cells, the same edit and archive actions — because these ARE recurring
                  expenses and a member should not have to learn a second table to read them.

                  The vehicle's own action is appended to each row's menu rather than replacing the
                  set: detaching removes the STATEMENT that this cost belongs to the vehicle and does
                  not touch the expense, which is a third thing beside editing and archiving it.

                  No sorting and no group handling: three rows have nothing to sort, and a group
                  cannot appear inside a vehicle's own costs because it IS the group.
                */}
                {detail.data.costs.length === 0 ? (
                  <EmptyState>{t("veh.cost.empty")}</EmptyState>
                ) : (
                  <ExpenseTable
                    className=""
                    rows={detail.data.costs}
                    members={memberList.data?.members ?? []}
                    onEdit={setEditingCost}
                    nextDue={schedule.data?.nextDue ?? {}}
                    extraActions={(row) => [
                      {
                        label: t("veh.cost.detach"),
                        destructive: true,
                        disabled: unlink.isPending,
                        onSelect: () => {
                          unlink.mutate({ vehicleId, expenseId: row.id });
                        },
                      },
                    ]}
                  />
                )}
              </Panel>

              <Panel
                title={t("veh.valuation.historyHeading")}
                description={t("veh.valuation.historyHint")}
                /*
                 * "Lost value per month" SITS WITH THE OBSERVATIONS IT IS COMPUTED FROM.
                 *
                 * It is the rate of change between two rows of this table, so it belongs beside
                 * them rather than in the figure row above — where it would read as a peer of
                 * "Change since purchase" and be taken for the same thing. It is not: one is a
                 * distance from a transaction, the other a speed between two measurements.
                 */
                aside={
                  vehicle.depreciation === null ? null : (
                    <p className="text-muted text-sm">
                      {t("veh.detail.perMonth", {
                        amount: formatEur(vehicle.depreciation.perMonthInEuros),
                      })}
                    </p>
                  )
                }
              >
                <ValuationHistory valuations={detail.data.valuations} />
              </Panel>
            </>
          ) : null}
        </div>
      </Page.Body>

      {(dialog === "cost" || editingCost !== null) && vehicle && (
        <ExpenseForm
          open
          editing={editingCost}
          currentAnchor={
            editingCost === null ? null : (schedule.data?.currentAnchor[editingCost.id] ?? null)
          }
          onClose={close}
          members={memberList.data?.members ?? []}
          cards={expenses.data?.cards ?? []}
          /*
           * PASSED UNCONDITIONALLY, and ignored while editing. `ExpenseForm` branches on `editing`
           * before it consults this — an edit is `PATCH /api/expenses/:id` wherever it was opened
           * from, because the row being changed is an expense and the link is not being touched.
           *
           * ONE REQUEST, ONE TRANSACTION on the create path. Posting the expense and then the link
           * would leave an orphan on any failure between them: a real recurring cost in the
           * household total, ungrouped, with nothing recording that it was meant to belong to the
           * car.
           */
          destination={{
            post: (client, body) =>
              client.post<{ id: string }>(`/vehicles/${vehicleId}/costs`, { expense: body }),
            // The vehicle's own page shows the row and its figure; the expenses key is already there.
            invalidates: [["vehicles"]],
            heading: t("veh.cost.heading", { name: vehicle.name }),
            submitLabel: t("veh.cost.submit"),
          }}
          onCreated={close}
        />
      )}

      {dialog === "valuation" && vehicle && (
        <ValuationForm
          open
          onClose={close}
          vehicleId={vehicleId}
          vehicleName={vehicle.name}
          firstRegisteredOn={vehicle.firstRegisteredOn}
        />
      )}

      {dialog === "sell" && vehicle && (
        <DisposalDialog
          open
          onClose={close}
          vehicleId={vehicleId}
          vehicleName={vehicle.name}
          lastValuationInEuros={vehicle.currentValueInEuros}
        />
      )}
    </Page>
  );
}

/** A signed euro figure. `VehicleList` states the argument for the explicit `+`. */
function signedEur(amountInEuros: number): string {
  return amountInEuros > 0 ? `+${formatEur(amountInEuros)}` : formatEur(amountInEuros);
}

/** One decimal place, signed. The server has already rounded; this only puts the sign on. */
function signedPercent(percent: number): string {
  const rendered = percent.toFixed(1).replace(".", ",");
  return percent > 0 ? `+${rendered}` : rendered;
}

/** One headline figure. The property detail page's `Figure`, with room for a qualifying line. */
function Figure({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string | undefined;
}): ReactNode {
  return (
    <div className="rounded-control border-line bg-panel border p-4">
      <dt className="text-muted text-sm">{label}</dt>
      <dd className="text-2xl font-semibold tabular-nums">{value}</dd>
      {hint === undefined ? null : <p className="text-muted mt-1 text-xs">{hint}</p>}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-3 border-line border-b py-2 last:border-b-0">
      <dt className="text-muted text-sm">{label}</dt>
      <dd className="text-sm font-medium">{value}</dd>
    </div>
  );
}
